import { useState } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line
} from "recharts";

const TEAL = "#0D5C63";
const TEAL2 = "#14B8C4";
const CARD_BG = "#EDEEF0";
const WHITE = "#FFFFFF";
const BORDER = "#D8DADF";
const DARK = "#1A1A2E";
const GRAY = "#6B7280";
const GRAY_MID = "#9CA3AF";
const RED = "#C0392B";
const RED_BG = "#FDECEA";
const AMBER = "#B45309";
const AMBER_BG = "#FEF3C7";
const GREEN = "#166534";
const GREEN_BG = "#DCFCE7";
const PIE_COLORS = ["#0D5C63", "#14B8C4", "#2DD4BF", "#5EEAD4", "#0F766E", "#059669"];

// Static data for standalone inventory route
const INVENTORY_DATA = [
  { brand: "Coastal Craft Cocktails", product: "Coastal Craft Cocktails Nordic Gin Tonic 355 Ml", code: "CST-G&T-355", abv: "7%", config: "24x 355", taxPaid: 551.83, inBond: 0, total: 551.83, allocated: 0, available: 551.83, vel3m: 1.333, vel6m: 1, mos: 413 },
  { brand: "Verde Cachaca", product: "Verde Cachaca Prata 750 Ml", code: "VRD-PRATA-750", abv: "42%", config: "6x 750", taxPaid: 375, inBond: 0, total: 406, allocated: 31, available: 375, vel3m: 147.333, vel6m: 179.17, mos: 2.55 },
  { brand: "Tierra Tequila", product: "Tierra Tequila Still Strength 750 Ml", code: "TRR-PLDB-750SS", abv: "50%", config: "6x 750", taxPaid: 373, inBond: 0, total: 373, allocated: 0, available: 373, vel3m: 1, vel6m: 2.17, mos: 373 },
  { brand: "Tierra Tequila", product: "Tequila Tierra Reposado 6 700 ABV 40", code: "TRR-PLDRP-700G", abv: "40%", config: "6x 700", taxPaid: 271, inBond: 0, total: 271, allocated: 0, available: 271, vel3m: 0, vel6m: 0.83, mos: 0 },
  { brand: "Coastal Craft Cocktails", product: "Coastal Craft Cocktails Passionfruit Caipirinha 355 Ml", code: "CST-CAI-355", abv: "7%", config: "24x 355", taxPaid: 223, inBond: 0, total: 223, allocated: 0, available: 223, vel3m: 1.333, vel6m: 1.83, mos: 167 },
  { brand: "Tierra Tequila", product: "Tierra Tequila Blanco 750 Ml", code: "TRR-PLDB-750", abv: "40%", config: "6x 700", taxPaid: 208.83, inBond: 0, total: 208.83, allocated: 0, available: 208.83, vel3m: 15.667, vel6m: 26.17, mos: 13.33 },
  { brand: "Provence Gin", product: "Provence Gin French Flag 12 750 Ml", code: "PRV-FF-750", abv: "43%", config: "12x 750", taxPaid: 197, inBond: 0, total: 197, allocated: 0, available: 197, vel3m: 0, vel6m: 0, mos: 0 },
  { brand: "Tierra Tequila", product: "Tierra Agave Amburana", code: "TRR-PLDAMB-750", abv: "0%", config: "6x 750", taxPaid: 161.5, inBond: 0, total: 161.5, allocated: 0, available: 161.5, vel3m: 3.667, vel6m: 7.17, mos: 44 },
  { brand: "Verde Cachaca", product: "Verde Cachaca Oak 750ml", code: "VRD-OAK-750", abv: "42%", config: "6x 750", taxPaid: 79, inBond: 0, total: 79, allocated: 0, available: 79, vel3m: 8, vel6m: 12, mos: 9.9 },
  { brand: "Fjord Aquavit", product: "Fjord Aquavit Swedish 750ml", code: "FJD-SWE-750", abv: "40%", config: "6x 750", taxPaid: 82, inBond: 0, total: 82, allocated: 0, available: 82, vel3m: 5.33, vel6m: 8.17, mos: 15.4 },
  { brand: "Fjord Aquavit", product: "Fjord Aquavit Danish 750ml", code: "FJD-DAN-750", abv: "40%", config: "6x 750", taxPaid: 65, inBond: 0, total: 65, allocated: 0, available: 65, vel3m: 4.0, vel6m: 6.5, mos: 16.3 },
  { brand: "Austral", product: "Austral Pisco", code: "AUS-PSC", abv: "40%", config: "6x 750", taxPaid: 44, inBond: 0, total: 44, allocated: 0, available: 44, vel3m: 0, vel6m: 0, mos: 0 }
];

const INV_MONTHLY = [
  { month: 1, taxPaid: 5100, available: 4800 },
  { month: 2, taxPaid: 8200, available: 7600 },
  { month: 3, taxPaid: 3474.56, available: 3474.56 }
];

export default function InventoryPage() {
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("All");
  const [sortCol, setSortCol] = useState("taxPaid");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("table");
  const [selectedSku, setSelectedSku] = useState(null);

  const brands = ["All", ...Array.from(new Set(INVENTORY_DATA.map((r) => r.brand)))];
  const filtered = INVENTORY_DATA
    .filter((r) => brandFilter === "All" || r.brand === brandFilter)
    .filter((r) => !search || r.product.toLowerCase().includes(search.toLowerCase()) || r.code.toLowerCase().includes(search.toLowerCase()) || r.brand.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol];
      if (typeof va === "string") return sortDir === "desc" ? vb.localeCompare(va) : va.localeCompare(vb);
      return sortDir === "desc" ? (vb || 0) - (va || 0) : (va || 0) - (vb || 0);
    });

  const totals = {
    taxPaid: filtered.reduce((s, r) => s + r.taxPaid, 0),
    inBond: filtered.reduce((s, r) => s + r.inBond, 0),
    total: filtered.reduce((s, r) => s + r.total, 0),
    allocated: filtered.reduce((s, r) => s + r.allocated, 0),
    available: filtered.reduce((s, r) => s + r.available, 0)
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortCol(col); setSortDir("desc"); }
  };

  const getHealth = (r) => {
    if (r.vel3m === 0 && r.vel6m === 0 && r.available > 0) return "No Sales";
    if (r.mos > 0 && r.mos <= 3) return "Low Stock";
    if (r.mos > 100) return "Over Stock";
    if (r.mos > 12) return "Over Stock";
    return "Healthy";
  };

  const healthCounts = { "No Sales": 0, "Low Stock": 0, "Over Stock": 0, "Healthy": 0 };
  filtered.forEach((r) => { const h = getHealth(r); healthCounts[h] = (healthCounts[h] || 0) + 1; });

  const brandTotals = brands.filter((b) => b !== "All").map((b) => {
    const items = INVENTORY_DATA.filter((r) => r.brand === b);
    return { brand: b, total: items.reduce((s, r) => s + r.total, 0), skus: items.length };
  }).sort((a, b) => b.total - a.total);

  const StatusPill = ({ status }) => {
    const cfg = {
      "No Sales": { bg: RED_BG, text: RED, border: "#FCA5A5" },
      "Low Stock": { bg: AMBER_BG, text: AMBER, border: "#FCD34D" },
      "Over Stock": { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
      "Healthy": { bg: GREEN_BG, text: GREEN, border: "#86EFAC" }
    };
    const c = cfg[status] || { bg: CARD_BG, text: GRAY, border: BORDER };
    return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700 }}>{status}</span>;
  };

  const SortTH = ({ col, children, right, w }) => (
    <th onClick={() => handleSort(col)} style={{ background: TEAL, color: WHITE, padding: "8px 10px", textAlign: right ? "right" : "left", fontSize: 10.5, fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", width: w }}>
      {children}{sortCol === col ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );

  return (
    <div style={{ padding: "16px 18px", background: "#F4F5F7", minHeight: "100vh" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: DARK, marginBottom: 12 }}>Inventory</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { l: "Total Available", v: totals.available.toFixed(2), sub: "(Total Warehouses)", color: TEAL, hi: true, change: "↓23%" },
          { l: "Tax Paid", v: totals.taxPaid.toFixed(2), sub: "(Total Warehouses)", color: DARK, change: "↓29%" },
          { l: "Held In Bond", v: totals.inBond.toFixed(2), sub: "(Total Warehouses)", color: DARK },
          { l: "Allocated", v: totals.allocated.toFixed(0), sub: "(Total Warehouses)", color: DARK },
          { l: "Total SKUs", v: filtered.length.toString(), sub: `${healthCounts["No Sales"]} no sales · ${healthCounts["Low Stock"]} low · ${healthCounts["Over Stock"]} over`, color: DARK }
        ].map((k, i) => (
          <div key={i} style={{ background: k.hi ? `${TEAL}10` : WHITE, border: `1px solid ${k.hi ? TEAL : BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{k.l}</div>
              {k.change && <span style={{ fontSize: 10, color: RED, fontWeight: 700 }}>{k.change}</span>}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color, marginTop: 4 }}>{k.v}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "0 0 280px" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search for inventory..." style={{ width: "100%", padding: "7px 12px 7px 32px", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, color: DARK, background: WHITE }} />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: GRAY_MID }}>🔍</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {brands.map((b) => (
            <button key={b} onClick={() => setBrandFilter(b)} style={{ padding: "5px 10px", border: `1px solid ${brandFilter === b ? TEAL : BORDER}`, borderRadius: 5, background: brandFilter === b ? TEAL : WHITE, color: brandFilter === b ? WHITE : DARK, fontSize: 11, fontWeight: brandFilter === b ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap" }}>
              {b === "All" ? "All Brands" : b.split(" ")[0]}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {[["table", "📋 Table"], ["charts", "📊 Charts"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: "5px 12px", border: `1px solid ${view === v ? TEAL : BORDER}`, borderRadius: 6, background: view === v ? TEAL : WHITE, color: view === v ? WHITE : GRAY, fontSize: 11, fontWeight: view === v ? 700 : 400, cursor: "pointer" }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {view === "table" && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <SortTH col="brand">Brand</SortTH>
                  <SortTH col="product">Product</SortTH>
                  <SortTH col="code">Item Code</SortTH>
                  <th style={{ background: TEAL, color: WHITE, padding: "8px 10px", fontSize: 10.5, fontWeight: 600, textAlign: "center" }}>ABV</th>
                  <th style={{ background: TEAL, color: WHITE, padding: "8px 10px", fontSize: 10.5, fontWeight: 600 }}>Case Config</th>
                  <SortTH col="taxPaid" right>Tax Paid</SortTH>
                  <SortTH col="inBond" right>In Bond</SortTH>
                  <SortTH col="total" right>Total</SortTH>
                  <SortTH col="allocated" right>Allocated</SortTH>
                  <SortTH col="available" right>Available</SortTH>
                  <SortTH col="vel3m" right>3-mo Avg</SortTH>
                  <SortTH col="vel6m" right>6-mo Avg</SortTH>
                  <SortTH col="mos" right>MOS</SortTH>
                  <th style={{ background: TEAL, color: WHITE, padding: "8px 10px", fontSize: 10.5, fontWeight: 600 }}>Health</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const health = getHealth(r);
                  return (
                    <tr key={i} onClick={() => setSelectedSku(selectedSku === i ? null : i)} style={{ background: selectedSku === i ? `${TEAL}08` : i % 2 === 0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}`, cursor: "pointer" }}>
                      <td style={{ padding: "7px 10px", fontWeight: 500, color: DARK, whiteSpace: "nowrap" }}>{r.brand.split(" ")[0]}</td>
                      <td style={{ padding: "7px 10px", color: DARK, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.product}</td>
                      <td style={{ padding: "7px 10px", color: GRAY, fontFamily: "monospace", fontSize: 10 }}>{r.code}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center", color: GRAY }}>{r.abv}</td>
                      <td style={{ padding: "7px 10px", color: GRAY, fontSize: 10 }}>{r.config}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{r.taxPaid.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", color: r.inBond === 0 ? GRAY_MID : DARK }}>{r.inBond.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{r.total.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", color: r.allocated === 0 ? GRAY_MID : AMBER, fontWeight: r.allocated > 0 ? 700 : 400 }}>{r.allocated.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: TEAL }}>{r.available.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", color: r.vel3m === 0 ? GRAY_MID : DARK }}>{r.vel3m > 0 ? r.vel3m.toFixed(3) : "—"}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", color: r.vel6m === 0 ? GRAY_MID : DARK }}>{r.vel6m > 0 ? r.vel6m.toFixed(2) : "—"}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600, color: r.mos > 100 ? RED : r.mos > 12 ? AMBER : r.mos > 0 ? GREEN : GRAY_MID }}>{r.mos > 0 ? (r.mos > 100 ? Math.round(r.mos) : r.mos.toFixed(1)) : "—"}</td>
                      <td style={{ padding: "7px 10px" }}><StatusPill status={health} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "charts" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 12 }}>Inventory by Brand</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={brandTotals} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                  <XAxis dataKey="brand" tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip formatter={(v) => [v.toFixed(2) + " cs", "Total"]} contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill={TEAL} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 12 }}>Product SKU Quantity</div>
              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, alignItems: "center" }}>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie data={filtered.filter((r) => r.total > 0).slice(0, 12)} dataKey="total" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={1}>
                      {filtered.filter((r) => r.total > 0).slice(0, 12).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => v.toFixed(2) + " cs"} contentStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ maxHeight: 170, overflowY: "auto" }}>
                  {filtered.filter((r) => r.total > 0).slice(0, 12).map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 0", fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                      <span style={{ color: DARK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.product.length > 35 ? r.product.slice(0, 35) + "..." : r.product}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: "center", fontSize: 18, fontWeight: 800, color: TEAL, marginTop: 4 }}>{totals.total.toFixed(2)} <span style={{ fontSize: 11, fontWeight: 400, color: GRAY }}>Total Quantity</span></div>
            </div>
          </div>

          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>Inventory Trend — 2026</div>
                <div style={{ fontSize: 11, color: GRAY }}>Tax Paid vs Available by month</div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {[[TEAL2, "Tax Paid"], [TEAL, "Available"]].map(([c, l]) => (
                  <div key={l} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: GRAY }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l}
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={INV_MONTHLY} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: GRAY }} tickFormatter={(v) => ["Jan", "Feb", "Mar"][v - 1]} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} width={42} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="taxPaid" name="Tax Paid" stroke={TEAL2} strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="available" name="Available" stroke={TEAL} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
